import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AdminSignIn, AdminNav } from "@/components/admin/AdminShell";
import { toCsv, downloadCsv } from "@/lib/csv-export";
import {
  listContactMessages,
  setContactMessageStatus,
  deleteContactMessage,
  resendContactEmail,
  type ContactMessage,
  type ContactStatus,
} from "@/lib/contact-messages.functions";

export const Route = createFileRoute("/admin/messages")({
  component: MessagesDashboard,
  head: () => ({
    meta: [
      { title: "Messages — SFN (internal)" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

const STATUS_STYLES: Record<ContactStatus, string> = {
  new: "bg-mint/15 text-mint",
  read: "bg-white/10 text-foreground",
  archived: "bg-white/5 text-muted-foreground",
  spam: "bg-coral/15 text-coral",
};

function MessagesDashboard() {
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [rows, setRows] = useState<ContactMessage[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    startDate: "",
    endDate: "",
  });

  const fetchList = useServerFn(listContactMessages);
  const updateStatus = useServerFn(setContactMessageStatus);
  const removeMessage = useServerFn(deleteContactMessage);
  const resendEmail = useServerFn(resendContactEmail);
  const [resending, setResending] = useState<number | null>(null);

  const onResend = async (id: number) => {
    setResending(id);
    try {
      const res = (await resendEmail({ data: { id } })) as {
        status: string;
        error: string | null;
        email_sent_at: string | null;
      };
      setRows((r) =>
        r.map((m) =>
          m.id === id
            ? {
                ...m,
                email_status: res.status,
                email_error: res.error,
                email_sent_at: res.email_sent_at ?? m.email_sent_at,
                email_attempts: (m.email_attempts ?? 0) + 1,
              }
            : m,
        ),
      );
      if (res.status === "queued" || res.status === "sent") {
        toast.success("Email re-queued for delivery.");
      } else if (res.status === "unconfigured") {
        toast.error("Email sending is not configured yet (sender domain not verified).");
      } else {
        toast.error(res.error ?? "Resend failed.");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Resend failed.");
    } finally {
      setResending(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setSignedIn(Boolean(data.session));
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setSignedIn(Boolean(session));
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const load = useCallback(() => {
    if (signedIn !== true) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    fetchList({ data: { ...filters, limit: 500 } })
      .then((res) => {
        setRows(res.rows as ContactMessage[]);
        setTotal(res.total);
        setLoading(false);
      })
      .catch(() => {
        setError("Could not load messages.");
        setLoading(false);
      });
  }, [fetchList, filters, signedIn]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { new: 0, read: 0, archived: 0, spam: 0 };
    for (const r of rows) c[r.status] = (c[r.status] ?? 0) + 1;
    return c;
  }, [rows]);

  const onStatus = async (id: number, status: ContactStatus) => {
    const prev = rows;
    setRows((r) => r.map((m) => (m.id === id ? { ...m, status } : m)));
    try {
      await updateStatus({ data: { id, status } });
      toast.success(`Marked as ${status}.`);
    } catch {
      setRows(prev);
      toast.error("Could not update message.");
    }
  };

  const onDelete = async (id: number) => {
    if (!window.confirm("Delete this message permanently?")) return;
    const prev = rows;
    setRows((r) => r.filter((m) => m.id !== id));
    try {
      await removeMessage({ data: { id } });
      toast.success("Message deleted.");
    } catch {
      setRows(prev);
      toast.error("Could not delete message.");
    }
  };

  const exportCsv = () => {
    if (rows.length === 0) {
      toast.error("Nothing to export.");
      return;
    }
    const csv = toCsv([
      ["id", "created_at", "status", "name", "email", "subject", "message", "lang", "path"],
      ...rows.map((r) => [
        r.id,
        r.created_at,
        r.status,
        r.name,
        r.email,
        r.subject,
        r.message.replace(/\r?\n/g, " "),
        r.lang ?? "",
        r.path ?? "",
      ]),
    ]);
    downloadCsv(`sfn-contact-messages-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    toast.success(`Exported ${rows.length} messages.`);
  };

  if (signedIn === false) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <h1 className="font-display text-3xl font-medium tracking-tight">Contact messages</h1>
        <AdminSignIn note="Internal inbox — contact submissions are only readable by an authenticated account." />
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <h1 className="font-display text-3xl font-medium tracking-tight">Contact messages</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Every “Join the Network” submission, stored as a durable backup.
      </p>
      <AdminNav current="messages" />

      {/* Filters */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setFilters((f) => ({ ...f, search: searchInput }));
        }}
        className="mt-8 flex flex-wrap items-end gap-3 rounded-2xl glass p-4"
      >
        <label className="flex-1 min-w-[220px] text-xs uppercase tracking-wider text-muted-foreground">
          Search
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="name, email, subject or message"
            className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground"
          />
        </label>
        <label className="text-xs uppercase tracking-wider text-muted-foreground">
          Status
          <select
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            className="mt-1 block rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground"
          >
            <option value="">All</option>
            <option value="new">New</option>
            <option value="read">Read</option>
            <option value="archived">Archived</option>
            <option value="spam">Spam</option>
          </select>
        </label>
        <label className="text-xs uppercase tracking-wider text-muted-foreground">
          From
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
            className="mt-1 block rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground"
          />
        </label>
        <label className="text-xs uppercase tracking-wider text-muted-foreground">
          To
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
            className="mt-1 block rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground"
          />
        </label>
        <button
          type="submit"
          className="rounded-full bg-white/[0.08] px-4 py-2 text-xs font-semibold hover:bg-white/[0.14]"
        >
          Apply
        </button>
        <button
          type="button"
          onClick={() => {
            setSearchInput("");
            setFilters({ search: "", status: "", startDate: "", endDate: "" });
          }}
          className="rounded-full px-4 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground"
        >
          Reset
        </button>
        <button
          type="button"
          aria-label="Download contact messages as a CSV file"
          onClick={exportCsv}
          className="rounded-full bg-gradient-to-br from-saffron to-coral px-4 py-2 text-xs font-semibold text-obsidian"
        >
          Export CSV
        </button>
      </form>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {[
          ["Total", total],
          ["New", counts.new],
          ["Read", counts.read],
          ["Archived", counts.archived],
          ["Spam", counts.spam],
        ].map(([label, value]) => (
          <div key={String(label)} className="rounded-2xl glass p-4">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="mt-1 font-display text-2xl">{value}</div>
          </div>
        ))}
      </div>

      {loading && <p className="mt-8 text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="mt-8 text-sm text-coral">{error}</p>}
      {!loading && !error && rows.length === 0 && (
        <p className="mt-8 text-sm text-muted-foreground">No messages match these filters.</p>
      )}

      <ul className="mt-6 space-y-3">
        {rows.map((m) => {
          const open = openId === m.id;
          return (
            <li key={m.id} className="rounded-2xl glass p-4">
              <button
                type="button"
                onClick={() => {
                  setOpenId(open ? null : m.id);
                  if (!open && m.status === "new") void onStatus(m.id, "read");
                }}
                aria-expanded={open}
                className="flex w-full flex-wrap items-center gap-3 text-left"
              >
                <span
                  className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${STATUS_STYLES[m.status]}`}
                >
                  {m.status}
                </span>
                <span className="font-medium">{m.subject}</span>
                <span className="text-sm text-muted-foreground">
                  {m.name} · {m.email}
                </span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {new Date(m.created_at).toLocaleString()}
                </span>
              </button>

              {open && (
                <div className="mt-4 border-t border-white/10 pt-4">
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
                    {m.message}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <a
                      href={`mailto:${m.email}?subject=${encodeURIComponent(`Re: ${m.subject}`)}`}
                      className="rounded-full bg-white/[0.08] px-3 py-1.5 text-xs font-semibold hover:bg-white/[0.14]"
                    >
                      Reply
                    </a>
                    {(["read", "archived", "spam"] as ContactStatus[]).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => void onStatus(m.id, s)}
                        disabled={m.status === s}
                        className="rounded-full bg-white/[0.06] px-3 py-1.5 text-xs font-semibold capitalize hover:bg-white/[0.12] disabled:opacity-40"
                      >
                        Mark {s}
                      </button>
                    ))}
                    {m.email_status !== "queued" && m.email_status !== "sent" && (
                      <button
                        type="button"
                        onClick={() => void onResend(m.id)}
                        disabled={resending === m.id}
                        className="rounded-full bg-emerald-400/15 px-3 py-1.5 text-xs font-semibold text-emerald-300 hover:bg-emerald-400/25 disabled:opacity-50"
                      >
                        {resending === m.id ? "Sending…" : "Resend email"}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => void onDelete(m.id)}
                      className="rounded-full bg-coral/15 px-3 py-1.5 text-xs font-semibold text-coral hover:bg-coral/25"
                    >
                      Delete
                    </button>
                  </div>
                  <p className="mt-3 text-[11px] text-muted-foreground">
                    Language: {m.lang ?? "—"} · Path: {m.path ?? "—"} · Email:{" "}
                    <span
                      className={
                        m.email_status === "queued" || m.email_status === "sent"
                          ? "text-emerald-300"
                          : m.email_status === "failed"
                            ? "text-red-300"
                            : "text-amber-300"
                      }
                    >
                      {m.email_status}
                    </span>
                    {m.email_sent_at
                      ? ` (${new Date(m.email_sent_at).toLocaleString()})`
                      : ""}
                    {m.email_error ? ` · ${m.email_error}` : ""}
                  </p>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </main>
  );
}
